import { Link } from "react-router-dom";
import { Mail, Phone, MapPin, Clock, Linkedin, Facebook, Instagram } from "lucide-react";
import { SITE, NAV_LINKS, SOCIALS } from "@/data/site";
import { CATEGORIES } from "@/data/products";
import { BurlyLogo } from "@/components/ui-burly/BurlyLogo";

export const Footer = () => {
  return (
    <footer
      data-testid="site-footer"
      className="bg-[#282760] text-[#E2E3EE]"
    >
      <div className="mx-auto max-w-[1440px] px-6 sm:px-10 lg:px-16 py-20">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <BurlyLogo size="h-12 w-36" surface="dark" testId="footer-burly-logo" />
            <p className="mt-6 max-w-md text-[#B7B8D0] leading-relaxed">
              {SITE.brand_line}. Premium fine fireclay kitchen and bathroom sinks for architects,
              distributors, and homeowners worldwide.
            </p>
            <div className="mt-8 space-y-3 text-sm text-[#B7B8D0]">
              <div className="flex items-start gap-3">
                <MapPin size={16} className="mt-1 text-white shrink-0" />
                <div>
                  <div className="text-[10px] uppercase tracking-[0.25em] text-white/80 font-semibold">{SITE.registered_office.label}</div>
                  <div>
                    {SITE.registered_office.line1}<br />
                    {SITE.registered_office.line2}, {SITE.registered_office.region}
                  </div>
                </div>
              </div>
              <div className="flex items-start gap-3 pt-2">
                <MapPin size={16} className="mt-1 text-white shrink-0" />
                <div>
                  <div className="text-[10px] uppercase tracking-[0.25em] text-white/80 font-semibold">{SITE.corporate_office.label}</div>
                  <div>
                    {SITE.corporate_office.line1}<br />
                    {SITE.corporate_office.line2}<br />
                    {SITE.corporate_office.region}
                  </div>
                </div>
              </div>
              <div className="flex items-start gap-3 pt-2">
                <MapPin size={16} className="mt-1 text-white shrink-0" />
                <div>
                  <div className="text-[10px] uppercase tracking-[0.25em] text-white/80 font-semibold">Factory</div>
                  <div>
                    {SITE.address.line1}<br />
                    {SITE.address.line2}<br />
                    {SITE.address.region}
                  </div>
                </div>
              </div>
              <a
                href={`tel:${SITE.phone_e164}`}
                data-testid="footer-phone"
                className="flex items-center gap-3 pt-2 hover:text-white transition-colors"
              >
                <Phone size={16} className="text-white" /> {SITE.phone_display}
              </a>
              <a
                href={`mailto:${SITE.email}`}
                data-testid="footer-email"
                className="flex items-center gap-3 hover:text-white transition-colors"
              >
                <Mail size={16} className="text-white" /> {SITE.email}
              </a>
              <div className="flex items-start gap-3">
                <Clock size={16} className="mt-1 text-white shrink-0" />
                <div>
                  {SITE.hours.map((h) => (
                    <div key={h.day}>{h.day}, {h.time}</div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="md:col-span-3">
            <div className="text-xs uppercase tracking-[0.2em] text-white font-semibold">
              Explore
            </div>
            <ul className="mt-6 space-y-3">
              {NAV_LINKS.map((l) => (
                <li key={l.to}>
                  <Link
                    to={l.to}
                    data-testid={`footer-link-${l.to.replace(/\//g, "") || "home"}`}
                    className="text-sm text-[#B7B8D0] hover:text-white transition-colors"
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-4">
            <div className="text-xs uppercase tracking-[0.2em] text-white font-semibold">
              Catalog
            </div>
            <ul className="mt-6 grid grid-cols-2 gap-3">
              {CATEGORIES.map((c) => (
                <li key={c.slug}>
                  <Link
                    to={`/products/?cat=${c.slug}`}
                    data-testid={`footer-cat-${c.slug}`}
                    className="text-sm text-[#B7B8D0] hover:text-white transition-colors"
                  >
                    {c.name}
                  </Link>
                </li>
              ))}
            </ul>
            <div className="mt-10">
              <div className="text-xs uppercase tracking-[0.2em] text-white font-semibold">Follow</div>
              <div className="mt-4 flex items-center gap-4">
                {SOCIALS.map((s) => {
                  const Icon = s.label === "LinkedIn" ? Linkedin : s.label === "Facebook" ? Facebook : Instagram;
                  return (
                    <a
                      key={s.label}
                      href={s.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={`Burly on ${s.label}`}
                      data-testid={`footer-social-${s.label.toLowerCase()}`}
                      className="p-2 border border-white/20 text-[#B7B8D0] hover:text-white hover:border-white/50 transition-colors"
                    >
                      <Icon size={16} />
                    </a>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-white/15 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="text-xs text-[#B7B8D0]">
            © {new Date().getFullYear()} {SITE.legal_name}. All rights reserved.
          </div>
          <div className="text-xs text-[#B7B8D0]">
            Premium Fine Fireclay Sinks.
            <span className="mx-2">·</span>
            <Link
              to="/sitemap/"
              data-testid="footer-sitemap-link"
              className="hover:text-white transition-colors underline underline-offset-4"
            >
              Sitemap
            </Link>
            <span className="mx-2">·</span>
            <a
              href="/sitemap.xml"
              data-testid="footer-sitemap-xml-link"
              className="hover:text-white transition-colors underline underline-offset-4"
            >
              XML
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};
